import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, CheckCircle2, Search, Eye, Smartphone, Folder, Globe, Download } from "lucide-react";

export interface MobileAppItem {
  id: number;
  category: string;
  title: string;
  description: string;
  tags: string[];
  url: string;
  detailsUrl?: string;
  status: string;
  iconUrl: string;
  highlightStat: { label: string; value: string };
  features: string[];
}

export const mobileAppsData: MobileAppItem[] = [
  {
    id: 1,
    category: "crm",
    title: "CRMPro By Hickter Perfex CRM",
    description: "CRM Pro – A smart customer relationship management solution designed to streamline sales, track leads, and manage client interactions in one place.",
    tags: ["React js", "php", "Tailwindcss", "MySQL"],
    url: "https://play.google.com/store/apps/details?id=com.crm.pro.app",
    detailsUrl: "/ourproducts/app/crmpro",
    status: "Google PlayStore Available",
    iconUrl: "https://play-lh.googleusercontent.com/gY20VDzqDBNx5yFFULUlAxipr7GLB59V2RHeyZ2Cyj40ffAkW30FjF2pmE8DqduIyHxxZY90wfMYrB2s2HbNag=w240-h480-rw",
    highlightStat: { label: "Category Type", value: "Business Management" },
    features: ["Lead & Pipeline Management", "Contact & Account Management", "Sales & Communication Automation", "Reporting, Analytics & Forecasting"]
  },
  {
    id: 2,
    category: "pos",
    title: "POS Hickter WooCommerce App",
    description: "A smart and user‑friendly Point of Sale app designed to help businesses manage sales, track inventory, and process payments seamlessly.",
    tags: ["Wordpress", "Woocommerce", "Tailwindcss", "php", "Custom API"],
    url: "https://play.google.com/store/apps/details?id=com.pos.hickter.app",
    detailsUrl: "/ourproducts/app/pos-woocommerce",
    status: "Google PlayStore Available",
    iconUrl: "https://ik.imagekit.io/filennky/Products%20Hickter/posapp.webp?updatedAt=1784371788310",
    highlightStat: { label: "Category Type", value: "POS System | Commerce" },
    features: ["Sales Management", "Inventory Tracking", "Payment Processing", "Reporting & Analytics"]
  },
  {
    id: 3,
    category: "storage",
    title: "Storeit – Keep It With You",
    description: "For Storeit – Keep It With You, a secure personal cloud storage solution, here are 4 essential core modules that make it safe, organized, and accessible:",
    tags: ["Reactjs", "Custom API", "Laravel", "Php", "Tailwindcss", "MySQL"],
    url: "https://play.google.com/store/apps/details?id=com.store.it.app",
    detailsUrl: "/ourproducts/app/storeit",
    status: "Google PlayStore Available",
    iconUrl: "https://ik.imagekit.io/filennky/Products%20Hickter/mylogo.png",
    highlightStat: { label: "Category Type", value: "Cloud Storage" },
    features: ["Secure File Storage", "File Organization", "Cross‑Device Sync", "Access Control & Sharing"]
  },
  {
    id: 4,
    category: "productivity",
    title: "Tracknn - Activity & Reminder",
    description: "Tracknn is your all-in-one companion for logging daily activities and setting intelligent reminders. Whether you’re managing a busy work schedule, tracking fitness goals, or simply making sure you never miss a deadline.",
    tags: ["JAVA", "php", "Tailwindcss", "MySQL"],
    url: "https://play.google.com/store/apps/details?id=com.tracknn.activity.app",
    detailsUrl: "/ourproducts/app/tracknn",
    status: "Google PlayStore Available",
    iconUrl: "https://ik.imagekit.io/filennky/Products%20Hickter/tracknn.png?updatedAt=1784371494392",
    highlightStat: { label: "Category Type", value: "Reminder & Activity Notes" },
    features: ["Activity Logging", "Smart Reminders", "Analytics & Insights", "Goal Tracking"]
  },
  {
    id: 5,
    category: "media",
    title: "MusicPro - Music & Audio Streaming App",
    description: "MusicPro is your ultimate music and audio streaming experience. Stream high-quality audio tracks, manage playlists, discover trending music, and enjoy offline playback seamlessly.",
    tags: ["Android", "php", "MySQL", "REST API", "Tailwindcss"],
    url: "https://play.google.com/store/apps/details?id=com.music.pro.app",
    detailsUrl: "/ourproducts/app/musicpro",
    status: "Google PlayStore Available",
    iconUrl: "https://ik.imagekit.io/filennky/Products%20Hickter/musicpro.webp",
    highlightStat: { label: "Category Type", value: "Music & Audio Streaming" },
    features: ["High Quality Audio Streaming", "Playlist & Library Management", "Offline Playback Mode", "Trending & Recommendations"]
  },
  {
    id: 6,
    category: "ecommerce",
    title: "eCom By Hickter",
    description: "Real-time sync e-commerce platform, fully customized for any online business — storefront, app and admin always in sync.",
    tags: ["Real-Time Sync", "Android Native", "React", "Node.js", "TailwindCSS"],
    url: "https://googledriveconvert.hickter.com/?download=1c5fSAroYQqEfN822-_vsyl3SvjvTQwF-",
    detailsUrl: "/ourproducts/app/ecom",
    status: "Live App & Storefront",
    iconUrl: "/assets/logo/ecom.png",
    highlightStat: { label: "Unified Sync", value: "Storefront, App & Admin" },
    features: ["Real-Time Sync", "Fully Customizable", "App + Website", "Orders & Inventory"]
  }
];

const CATEGORIES = [
  { id: 1, label: "All Products", slug: "all" },
  { id: 2, label: "E-Commerce", slug: "ecommerce" },
  { id: 3, label: "Business & CRM", slug: "crm" },
  { id: 4, label: "POS & Commerce", slug: "pos" },
  { id: 5, label: "Cloud Storage", slug: "storage" },
  { id: 6, label: "Productivity & Notes", slug: "productivity" },
  { id: 7, label: "Music & Media", slug: "media" }
];

export default function AppsProducts({ items = mobileAppsData }: { items?: MobileAppItem[] }) {
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredCards = useMemo(() => {
    return items.filter((item) => {
      if ((item as any).isVisible === false) return false;
      const matchesCategory = activeTab === "all" || item.category === activeTab;
      const matchesSearch =
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));

      return matchesCategory && matchesSearch;
    });
  }, [activeTab, searchQuery, items]);

  const handleReset = () => {
    setActiveTab("all");
    setSearchQuery("");
  };

  return (
    <div className="space-y-6 font-sans">
      {/* FILTER & SEARCH CONTROL BAR */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        {/* Category Pills */}
        <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveTab(cat.slug)}
              className={`px-3.5 py-1.5 text-xs font-semibold rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                activeTab === cat.slug
                  ? "bg-blue-600 dark:bg-blue-500 !text-white dark:!text-white shadow-md shadow-blue-500/20 font-extrabold"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-700 font-bold"
              }`}
            >
              <Folder size={13} className={activeTab === cat.slug ? "!text-white dark:!text-white" : "text-blue-600 dark:text-blue-400"} />
              <span className={activeTab === cat.slug ? "!text-white dark:!text-white font-extrabold" : "text-slate-800 dark:text-slate-200"}>{cat.label}</span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={14} />
          <input
            type="text"
            placeholder="Search mobile apps..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-xs font-medium pl-9 pr-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 focus:border-blue-500 outline-none transition-colors"
          />
        </div>
      </div>

      {/* MOBILE APPS GRID */}
      {filteredCards.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCards.map((item) => (
            <div
              key={item.id}
              className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-md hover:shadow-xl hover:border-blue-500/50 dark:hover:border-blue-500/50 transition-all flex flex-col justify-between space-y-5"
            >
              <div className="space-y-4">
                {/* Header Badge & Icon */}
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 p-2 flex items-center justify-center shadow-xs">
                    <img
                      src={item.iconUrl}
                      alt={item.title}
                      className="w-full h-full object-contain rounded-lg"
                      onError={(e: any) => {
                        e.target.onerror = null;
                        e.target.src = "https://ik.imagekit.io/filennky/Products%20Hickter/crmsuite.png?updatedAt=1784371494392";
                      }}
                    />
                  </div>
                  <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-200 dark:border-emerald-500/20">
                    ● {(item as any).customBadge || item.status}
                  </span>
                </div>

                {/* Title & Description */}
                <div className="space-y-1.5">
                  <h3 className="text-lg font-extrabold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-cyan-400 transition-colors leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed font-medium">
                    {item.description}
                  </p>
                </div>

                {/* Highlight Stat */}
                <div className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex items-center justify-between">
                  <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">
                    {item.highlightStat.label}
                  </span>
                  <span className="text-xs font-black text-blue-600 dark:text-cyan-400">
                    {item.highlightStat.value}
                  </span>
                </div>

                {/* Features */}
                <div className="space-y-1.5 pt-1">
                  <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500 block">
                    Core Capabilities:
                  </span>
                  <div className="grid grid-cols-1 gap-1">
                    {item.features.map((feat, fIdx) => (
                      <div key={fIdx} className="flex items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-300">
                        <CheckCircle2 size={13} className="text-blue-600 dark:text-cyan-400 shrink-0" />
                        <span className="truncate">{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tech Tags */}
                <div className="flex flex-wrap gap-1.5 pt-2">
                  {item.tags.map((tag, tIdx) => (
                    <span key={tIdx} className="text-[10px] font-bold text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 px-2.5 py-0.5 rounded border border-blue-200 dark:border-blue-500/20">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-2">
                {item.detailsUrl && (item as any).showDetailsBtn !== false && (
                  <Link
                    to={item.detailsUrl}
                    className="btn-outline flex-1 min-w-[110px] py-2 px-3 text-xs font-bold rounded-xl flex items-center justify-center gap-1.5"
                  >
                    <Eye size={14} />
                    <span>View Details</span>
                  </Link>
                )}
                {((item as any).playStoreUrl || item.url) && (item as any).showPlaystoreBtn !== false && (
                  <a
                    href={(item as any).playStoreUrl || item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary-blue flex-1 min-w-[110px] py-2 px-3 text-xs font-bold rounded-xl flex items-center justify-center gap-1.5 !text-white shadow-md shadow-blue-500/20"
                  >
                    <Smartphone size={14} className="!text-white" />
                    <span className="!text-white">PlayStore</span>
                    <ExternalLink size={12} className="!text-white" />
                  </a>
                )}
                {(item as any).websiteUrl && (item as any).showWebsiteBtn !== false && (
                  <a
                    href={(item as any).websiteUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-outline flex-1 min-w-[100px] py-2 px-3 text-xs font-bold rounded-xl flex items-center justify-center gap-1.5"
                  >
                    <Globe size={14} />
                    <span>Web Demo</span>
                    <ExternalLink size={12} />
                  </a>
                )}
                {(item as any).apkUrl && (
                  <a
                    href={(item as any).apkUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-outline py-2 px-3 text-xs font-bold rounded-xl flex items-center justify-center gap-1.5"
                    title="Download APK"
                  >
                    <Download size={14} />
                    <span>APK</span>
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
          <p className="text-slate-600 dark:text-slate-400 text-sm font-semibold">No mobile apps match your filter parameters.</p>
          <button onClick={handleReset} className="btn-outline text-xs px-4 py-2 rounded-xl">
            Reset Filters
          </button>
        </div>
      )}
    </div>
  );
}
